/**
 * Phase categorization system for PilotCity authentication flow
 * Supports sophisticated conditional flows with one-way gates and dynamic visibility
 */

import type { AuthPhase } from '@/composables/useAuthOrchestrator';

/**
 * Phase categories define behavior and navigation rules
 */
export enum PhaseCategory {
  REQUIRED = 'required', // Always shown, always required
  OPTIONAL = 'optional', // Shown but skippable
  CONDITIONAL = 'conditional', // Only shown when conditions met
  CRITICAL = 'critical', // Cannot navigate back once completed
  REPEATABLE = 'repeatable' // Can be revisited multiple times
}

/**
 * Phase configuration interface with metadata and navigation rules
 */
export interface AuthPhaseConfig {
  phase: AuthPhase;
  category: PhaseCategory;
  label: string;
  description: string;
  icon?: string;

  // Navigation rules
  prerequisites?: AuthPhase[];
  conditions?: (state: any) => boolean;
  allowBackNavigation?: boolean;
  requiresConfirmation?: boolean;

  // Visual styling
  color?: string;
  showInStepper?: boolean;

  // Behavioral flags
  isOneWayGate?: boolean;
  locksPreviousPhases?: boolean;

  // Validation and cleanup
  validator?: (state: any) => boolean;
  cleanup?: () => void;
}

/**
 * State interface for enhanced phase tracking
 */
export interface EnhancedPhaseState {
  // Existing navigation state
  phase: AuthPhase;
  phaseHistory: AuthPhase[];
  completedPhases: Set<AuthPhase>;
  maxReachedPhase: AuthPhase;

  // Enhanced conditional flow tracking
  encounteredPhases: Set<AuthPhase>; // All phases user has seen
  criticalDecisions: Map<AuthPhase, any>; // One-way decisions made
  lockedPhases: Set<AuthPhase>; // Phases that cannot be revisited
  conditionalTriggers: Map<string, any>; // Data that triggered conditional phases
}

/**
 * Phase visibility conditions
 */
export type PhaseCondition = (state: EnhancedPhaseState) => boolean;

/**
 * Trigger types for conditional phases
 */
export enum ConditionalTrigger {
  ACCOUNT_DISCOVERY = 'account_discovery',
  IDENTITY_CONFLICT = 'identity_conflict',
  DUPLICATE_EMAIL = 'duplicate_email',
  DUPLICATE_PHONE = 'duplicate_phone',
  MERGE_REQUIRED = 'merge_required'
}

/**
 * Phase transition result
 */
export interface PhaseTransitionResult {
  success: boolean;
  targetPhase: AuthPhase;
  lockedPhases?: AuthPhase[];
  criticalDecision?: any;
  error?: string;
}
